import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { orgsAPI, adminAPI, invitationsAPI } from '../api/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);        // Supabase user object
  const [currentOrg, setCurrentOrg] = useState(null); // { id, name, slug, role }
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [orgLoadError, setOrgLoadError] = useState(false);
  const [orgLoading, setOrgLoading] = useState(true); // true until first bootstrap completes

  // Check for pending invite token in sessionStorage and accept it
  const checkPendingInvite = useCallback(async () => {
    const pendingToken = sessionStorage.getItem('pendingInviteToken');
    if (!pendingToken) return;
    try {
      const result = await invitationsAPI.accept(pendingToken);
      // Switch to the new org
      localStorage.setItem('currentOrgId', result.organization_id);
    } catch {
      // Token may be expired or already used — silently ignore
    } finally {
      sessionStorage.removeItem('pendingInviteToken');
    }
  }, []);

  // Fetch platform admin flag from profile
  const fetchAdminFlag = useCallback(async () => {
    try {
      const profile = await adminAPI.getProfile();
      setIsPlatformAdmin(!!profile.is_platform_admin);
    } catch {
      setIsPlatformAdmin(false);
    }
  }, []);

  // Bootstrap org from Supabase user session
  const bootstrapOrg = useCallback(async (supabaseUser) => {
    setOrgLoading(true);
    try {
      if (!supabaseUser) { setCurrentOrg(null); setIsPlatformAdmin(false); setOrgLoadError(false); return; }

      // Handle pending invite before fetching orgs
      await checkPendingInvite();

      // Fetch admin flag in parallel
      fetchAdminFlag();

      // Try to restore previously selected org from localStorage
      const savedOrgId = localStorage.getItem('currentOrgId');

      setOrgLoadError(false);
      const orgs = await orgsAPI.listMine();
      if (orgs.length === 0) {
        // No org yet — caller (Register page) will create one
        setCurrentOrg(null);
        return;
      }
      const org = savedOrgId
        ? orgs.find(o => o.id === savedOrgId) || orgs[0]
        : orgs[0];
      setCurrentOrg(org);
      localStorage.setItem('currentOrgId', org.id);
    } catch (err) {
      console.error('bootstrapOrg failed:', err);
      setOrgLoadError(true);
      setCurrentOrg(null);
    } finally {
      setOrgLoading(false);
    }
  }, [checkPendingInvite, fetchAdminFlag]);

  // Listen to Supabase auth state changes
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      bootstrapOrg(session?.user ?? null).finally(() => setLoading(false));
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (!session?.user) {
        setCurrentOrg(null);
        localStorage.removeItem('currentOrgId');
      } else if (_event === 'SIGNED_IN' || _event === 'TOKEN_REFRESHED') {
        bootstrapOrg(session.user);
      }
    });
    return () => subscription.unsubscribe();
  }, [bootstrapOrg]);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    await bootstrapOrg(data.user);
    return { success: true };
  };

  // Sign in with Google (Supabase OAuth). Redirects to Google, then back to
  // /dashboard where getSession() + onAuthStateChange pick up the session and
  // bootstrap the org. Requires the Google provider enabled in Supabase Auth.
  const loginWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
        queryParams: { prompt: 'select_account' },
      },
    });
    if (error) throw error;
    return { success: true };
  };

  const register = async (email, password, fullName) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } },
    });
    if (error) throw error;
    return { success: true, user: data.user };
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setCurrentOrg(null);
    localStorage.removeItem('currentOrgId');
  };

  const switchOrg = (org) => {
    setCurrentOrg(org);
    localStorage.setItem('currentOrgId', org.id);
  };

  const refreshOrg = () => bootstrapOrg(user);

  return (
    <AuthContext.Provider value={{ user, currentOrg, orgLoadError, orgLoading, isPlatformAdmin, loading, login, loginWithGoogle, register, logout, switchOrg, refreshOrg }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
